import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { GradientHoverCard } from '@/components/common/GradientHoverCard'
import { FilmThumbnail } from '@/components/common/FilmThumbnail'
import type { FilmDocument } from '@/types'

interface FilmCardProps {
  film: FilmDocument & { id: string }
}

export function FilmCard({ film }: FilmCardProps) {
  return (
    <GradientHoverCard className="rounded-xl">
    <Link
      to={`/films/${film.slug}`}
      className="group relative block m-[2px] overflow-hidden rounded-[10px]"
    >
      {/* Thumbnail — custom upload takes priority, falls back to YouTube CDN */}
      <div className="relative overflow-hidden bg-black pt-[56.25%]">
        <FilmThumbnail
          title={film.title}
          thumbnailUrl={film.thumbnailUrl}
          youtubeId={film.youtubeId}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
        {(film.type ?? 'episode') === 'episode' && (
          <div className="absolute bottom-2 left-2 flex gap-1">
            <Badge className="text-xs bg-brand-blue text-brand-black border-0">S{film.seasonNumber}</Badge>
            <Badge className="text-xs bg-brand-orange text-brand-black border-0">EP{film.episodeNumber}</Badge>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 space-y-3">
        <h3 className="font-semibold leading-tight group-hover:text-primary transition-colors">
          {film.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{film.description}</p>

        {film.choreographers.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {film.choreographers.slice(0, 3).map((c) => (
                <Avatar key={c.slug} className="h-6 w-6 border-2 border-background">
                  <AvatarImage src={c.photoUrl} alt={c.name} />
                  <AvatarFallback className="text-[10px]">
                    {c.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
            <span className="text-xs text-muted-foreground">
              {film.choreographers.map((c) => c.name).join(', ')}
            </span>
          </div>
        )}
      </div>
    </Link>
    </GradientHoverCard>
  )
}
